/**
 * droppedFiles - turn a drop (or a paste) into real File objects, whatever
 * app it came from (Sharif, 10/22/26: "make sure that i am able to drag files
 * over from other site like teams or outlook ... for now, it only lets me drag
 * and drop from my folders").
 *
 * What a browser (Chrome/Edge on Windows) actually gets from each source:
 *  - File Explorer: real files in dataTransfer.files / items.
 *  - Classic Outlook (desktop): an attachment or a whole email is a VIRTUAL
 *    file (FileGroupDescriptor). Chromium hands it over as an item of kind
 *    'file' - read through items -> getAsFile(), since `files` can be empty.
 *    An email arrives as "<subject>.msg".
 *  - New Outlook / Outlook on the web / Teams: web apps. A drag carries only
 *    strings - text/uri-list, text/html (<a href>, <img src>), text/plain and
 *    sometimes DownloadURL ("mime:filename:url"). Their files live on
 *    SharePoint / OneDrive / the mail service and need the person's Microsoft
 *    sign-in, which this page cannot use for those hosts - those come back
 *    as `links` so the Doc Hub can say how to get them in, never silently.
 *
 * Two steps because the browser only lets a page read a DataTransfer DURING
 * the event: snapshotTransfer() runs synchronously in the drop/paste handler,
 * resolveSnapshot() does the slow part (fetching data:/blob:/https URLs)
 * afterwards. Everything is fetched HERE, in the browser, with the page's own
 * rules: a cross-origin URL goes out with no cookies and no token (CORS
 * decides), same-origin keeps the app's auth. There is deliberately no server
 * endpoint that fetches a URL for us (that would be an SSRF hole).
 */

const MAX_URLS = 20;
const FETCH_TIMEOUT_MS = 20000;

/** Hosts whose files need a Microsoft sign-in: never fetched, returned as links. */
const MS_HOST =
  /(^|\.)(sharepoint\.com|sharepoint-df\.com|onedrive\.com|1drv\.ms|live\.com|office\.com|office\.net|office365\.com|outlook\.com|microsoft\.com|microsoftonline\.com|skype\.com|svc\.ms|cloud\.microsoft|teams\.microsoft\.com|microsoftusercontent\.com)$/i;

const EXT_OF_MIME = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/svg+xml': 'svg',
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/zip': 'zip',
};

/** Which effect to show while dragging. Forcing 'copy' on a source that only
 *  allows 'link' (some web apps) would make the browser show "not allowed". */
export function pickDropEffect(effectAllowed) {
  const a = String(effectAllowed || 'all').toLowerCase();
  if (a === 'none') return 'none';
  if (/copy|all|uninitialized/.test(a)) return 'copy';
  if (/link/.test(a)) return 'link';
  if (/move/.test(a)) return 'move';
  return 'copy';
}

/** onDragOver for a file drop zone: allow the drop, show the copy cursor. */
export function acceptDrag(e) {
  e.preventDefault();
  const dt = e.dataTransfer;
  if (!dt) return;
  try {
    dt.dropEffect = pickDropEffect(dt.effectAllowed);
  } catch {
    /* read-only in some browsers */
  }
}

const safeGet = (dt, type) => {
  try {
    return (dt && typeof dt.getData === 'function' && dt.getData(type)) || '';
  } catch {
    return '';
  }
};

const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};

/** Files straight off a DataTransfer: items first (that is where Outlook's
 *  virtual files are), then the plain files list. Synchronous. */
export function filesOf(dt, { renameGeneric = false } = {}) {
  if (!dt) return [];
  let out = [];
  try {
    Array.from(dt.items || []).forEach((it) => {
      if (it && it.kind === 'file' && typeof it.getAsFile === 'function') {
        const f = it.getAsFile();
        if (f) out.push(f);
      }
    });
  } catch {
    /* no items API */
  }
  if (!out.length) {
    try {
      out = Array.from(dt.files || []).filter(Boolean);
    } catch {
      out = [];
    }
  }
  // A pasted screenshot is always called "image.png": give it a name that
  // says when, so three pastes are not three identical "image.png" cards.
  if (renameGeneric) {
    out = out.map((f) => {
      const m = /^image\.(png|jpe?g|gif|bmp|webp)$/i.exec(f.name || '');
      if (!m) return f;
      try {
        return new File([f], `Pasted image ${stamp()}.${m[1].toLowerCase()}`, { type: f.type });
      } catch {
        return f;
      }
    });
  }
  return out;
}

const isUrl = (s) => /^(https?:|data:|blob:)/i.test(String(s || '').trim());

const looksLikeFileName = (s) => /\.[a-z0-9]{2,5}$/i.test(String(s || '').trim()) && String(s).trim().length <= 200;

/** Every URL a drop names, best first, with a file name when the source gave one. */
function urlsOf(dt) {
  const found = [];
  const add = (url, name) => {
    const u = String(url || '').trim();
    if (!isUrl(u)) return;
    const prev = found.find((x) => x.url === u);
    if (prev) {
      if (!prev.name && name) prev.name = name;
      return;
    }
    found.push({ url: u, name: name ? String(name).trim() : '' });
  };

  // DownloadURL = "mime:filename:url" (Gmail/Outlook-web style "drag to desktop").
  const dl = safeGet(dt, 'DownloadURL');
  if (dl) {
    const m = /^([^:]*):([^:]*):(.+)$/.exec(dl.trim());
    if (m) add(m[3], m[2]);
  }

  // text/html: an image keeps its picture, a link keeps its text as the name.
  const html = safeGet(dt, 'text/html');
  if (html && typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc
        .querySelectorAll('img[src]')
        .forEach((img) =>
          add(img.getAttribute('src'), img.getAttribute('alt') && looksLikeFileName(img.getAttribute('alt')) ? img.getAttribute('alt') : '')
        );
      doc.querySelectorAll('a[href]').forEach((a) => {
        const text = a.getAttribute('download') || a.textContent || '';
        add(a.getAttribute('href'), looksLikeFileName(text) ? text : '');
      });
    } catch {
      /* not parseable */
    }
  }

  safeGet(dt, 'text/uri-list')
    .split(/\r?\n/)
    .forEach((line) => {
      if (line && !line.startsWith('#')) add(line);
    });

  const plain = safeGet(dt, 'text/plain').trim();
  if (plain && !/\s/.test(plain)) add(plain);

  return found.slice(0, MAX_URLS);
}

/**
 * Read everything usable out of a DataTransfer / clipboardData. MUST be called
 * synchronously inside the event handler.
 * -> { files: File[], urls: [{url, name}], hadData: bool }
 * Files win: when the source handed over real files, its URLs/HTML are just
 * a description of the same thing.
 */
export function snapshotTransfer(dt, opts = {}) {
  const files = filesOf(dt, opts);
  let types = [];
  try {
    types = Array.from((dt && dt.types) || []);
  } catch {
    types = [];
  }
  if (files.length) return { files, urls: [], hadData: true };
  return { files: [], urls: urlsOf(dt), hadData: types.length > 0 };
}

function hostOf(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

/** Where a link came from, in words a person recognises. */
export function sourceOf(url) {
  const h = hostOf(url).toLowerCase();
  if (!h) return 'another app';
  if (/sharepoint|onedrive|1drv\.ms|svc\.ms/.test(h)) return 'SharePoint / OneDrive';
  if (/teams|skype/.test(h)) return 'Teams';
  if (/outlook|office365|office\.net|microsoftusercontent/.test(h)) return 'Outlook';
  return h;
}

const nameFromUrl = (url) => {
  try {
    const u = new URL(url);
    // SharePoint "open" links keep the real name in ?file= / ?id=
    const q = u.searchParams.get('file') || u.searchParams.get('id') || '';
    const fromQ = q ? decodeURIComponent(q.split('/').pop()) : '';
    if (looksLikeFileName(fromQ)) return fromQ;
    const last = decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() || '');
    return looksLikeFileName(last) ? last : '';
  } catch {
    return '';
  }
};

const fileName = (want, url, mime) => {
  const n = String(want || '').trim() || nameFromUrl(url);
  if (n) return n.replace(/[\\/:*?"<>|]+/g, '_');
  const ext =
    EXT_OF_MIME[
      String(mime || '')
        .split(';')[0]
        .trim()
        .toLowerCase()
    ] || 'bin';
  return `${/^image\//.test(mime) ? 'Dropped image' : 'Dropped file'} ${stamp()}.${ext}`;
};

/** data:[mime][;base64],payload -> File, no network. */
export function fileFromDataUrl(url, name) {
  const m = /^data:([^,]*?),(.*)$/is.exec(url);
  if (!m) return null;
  const meta = m[1].split(';');
  const mime = meta[0] || 'text/plain';
  const isB64 = meta.slice(1).some((p) => p.toLowerCase() === 'base64');
  let bytes;
  try {
    if (isB64) {
      const bin = atob(m[2].replace(/\s+/g, ''));
      bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(m[2]));
    }
  } catch {
    return null;
  }
  return new File([bytes], fileName(name, '', mime), { type: mime });
}

function filenameFromDisposition(cd) {
  if (!cd) return '';
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
  return plain ? plain[1].trim() : '';
}

async function fetchAsFile(url, name, fetchImpl, sameOrigin) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : null;
  try {
    let target = url;
    // Same origin: go through the app's own fetch with a relative /api/ path
    // so it carries the sign-in token like every other call.
    if (sameOrigin) {
      const u = new URL(url);
      target = u.pathname + u.search;
    }
    const r = await fetchImpl(target, {
      credentials: sameOrigin ? 'same-origin' : 'omit',
      ...(sameOrigin ? {} : { mode: 'cors' }),
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    if (!r || !r.ok) return null;
    const blob = await r.blob();
    // A login page or an error page is not the file somebody dropped.
    if (!blob || (blob.type || '').startsWith('text/html')) return null;
    const cd = r.headers && typeof r.headers.get === 'function' ? r.headers.get('content-disposition') : '';
    return new File([blob], fileName(filenameFromDisposition(cd) || name, url, blob.type), { type: blob.type });
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Turn a snapshot's URLs into files where the browser is allowed to.
 * -> { files: File[], links: [{url, name, source}] }
 * `links` are what could not be downloaded here (SharePoint/OneDrive/Teams/
 * Outlook, or any site that refuses a cross-origin read).
 */
export async function resolveSnapshot(snap, { fetchImpl, origin } = {}) {
  const doFetch = fetchImpl || (typeof window !== 'undefined' ? window.fetch.bind(window) : null);
  const here = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  const files = [...(snap.files || [])];
  const links = [];
  for (const { url, name } of snap.urls || []) {
    let file = null;
    if (/^data:/i.test(url)) {
      file = fileFromDataUrl(url, name);
    } else if (/^blob:/i.test(url)) {
      // Only works for a blob made by THIS page; another app's blob: is not ours to read.
      file = doFetch ? await fetchAsFile(url, name, doFetch, false) : null;
    } else {
      const host = hostOf(url);
      let sameOrigin = false;
      try {
        sameOrigin = new URL(url).origin === here;
      } catch {
        /* bad url */
      }
      if (doFetch && host && (sameOrigin || !MS_HOST.test(host))) {
        file = await fetchAsFile(url, name, doFetch, sameOrigin);
      }
    }
    if (file) files.push(file);
    else if (!/^data:/i.test(url)) {
      links.push({
        url: /^blob:/i.test(url) ? '' : url,
        name: name || nameFromUrl(url),
        source: /^blob:/i.test(url) ? 'another app' : sourceOf(url),
      });
    }
  }
  return { files, links };
}

/** One call when the caller can wait: snapshot now, resolve after. */
export function filesFromDataTransfer(dt, opts = {}) {
  const snap = snapshotTransfer(dt, opts);
  return resolveSnapshot(snap, opts);
}
